import { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Container,
  Grid,
  GridCol,
  Group,
  Select,
  TextInput,
  Title,
  useMantineColorScheme
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { createUser, IUser, PermissionOption, updateUser } from '../../../libraries/user/index';
import { fetchRegions } from '../../../libraries/region/index';
import { ILanguage, fetchLanguages } from '../../../libraries/language/index';
import { Language } from '../../languages/languageHandler';
import LocationTable from '../../components/locations-table';
import { useSelector } from 'react-redux';
import { AppState } from '../../client-redux/reducers';
import { ILocation } from '../../../libraries/location';
import { permissionsToUser } from './permissions';
import UpdateUserConfirmation from '../profile/update-user-confirmation';
import * as Icon from '@tabler/icons-react';
import './styles.scss';

export enum UserFormMode {
  CREATE,
  UPDATE,
  UPDATE_PROFILE
}

interface UserFormProps {
  mode: UserFormMode;
  isNew: boolean;
  user: IUser;
  lang: Language;
  permissionOptions: PermissionOption[];
  togglePermissions: (permissions: PermissionOption) => void;
  onSuccess?: () => void;
  onError?: (message: string) => void;
  className?: string;
  entities?: { entid: number; name: string }[];
  onDelete?: (uid: number) => void;
  changeLanguage?: (langid: number) => void;
}

interface ThemeItem {
  value: string;
  label: string;
  icon: React.ReactNode;
}

const UserForm = ({
  mode,
  isNew,
  user,
  lang,
  permissionOptions,
  togglePermissions,
  onSuccess,
  onError,
  entities,
  onDelete,
  changeLanguage
}: UserFormProps) => {
  const currentEntid = useSelector<AppState, number>(state => state.filters.filterBar.entity ?? 0);
  const locations = useSelector<AppState, ILocation[]>(state => state.locations.locations);
  const [regions, setRegions] = useState<string[]>([]);
  const [languages, setLanguages] = useState<ILanguage[]>([]);
  const [openPassword, setOpenPassword] = useState<boolean>(false);
  const [openPin, setOpenPin] = useState<boolean>(false);
  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const [selectedPermissions, setSelectedPermissions] = useState<PermissionOption[]>([]);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [pendingUserUpdate, setPendingUserUpdate] = useState<IUser | null>(null);
  const toggleConfirmModal = () => setOpenConfirmModal(prev => !prev);

  const { colorScheme, setColorScheme } = useMantineColorScheme({
    keepTransitions: true
  });
  const dark = colorScheme === 'dark';

  useEffect(() => {
    if (selectedPermissions.length === 0) {
      setSelectedPermissions(
        permissionOptions.map(permission => ({
          ...permission,
          selected:
            user.permissions?.some(p => p.appId === permission.appId && (p.permissionCode & permission.mask) !== 0) ||
            false
        }))
      );
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, permissionOptions]);

  useEffect(() => {
    fetchRegions().then(response => {
      setRegions(response.regions);
    });
    fetchLanguages().then(response => {
      setLanguages(response.languages);
    });
  }, []);

  const form = useForm({
    validateInputOnChange: true,
    initialValues: {
      entid: user?.entid ?? 0,
      uid: user?.uid ?? 0,
      gid: user?.gid ?? 0,
      name: user?.name ?? '',
      mobile: user?.mobile ?? '',
      email: user?.email ?? '',
      card: user?.card ?? '',
      lang: user?.lang ?? 0,
      region: user?.region ?? '',
      password: '',
      passwordConfirm: '',
      pin: '',
      pinConfirm: '',
      permissions: user?.permissions ?? [],
      shops: user?.shops ?? []
    },
    validate: {
      passwordConfirm: (value, values) => {
        if (values.password && value !== values.password) {
          return `${lang.USER_NEW_PASSWORD_ERROR}`;
        }
        return null;
      },
      pinConfirm: (value, values) => {
        if (values.pin && value !== values.pin) {
          return `${lang.USER_NEW_PIN_ERROR}`;
        }
        return null;
      }
    }
  });

  //Function that ensures values are cleared
  const clearSensitiveFields = () => {
    form.setValues({
      ...form.values,
      password: '',
      passwordConfirm: '',
      pin: '',
      pinConfirm: ''
    });
    form.setErrors({});
  };

  const getPermissionLabel = (langKey: string, lang: any) => {
    return lang[langKey];
  };

  const toggleSelection = (permission: PermissionOption) => {
    const updated = selectedPermissions.map(p =>
      p.appId === permission.appId && p.mask === permission.mask ? { ...p, selected: !p.selected } : p
    );
    setSelectedPermissions(updated);
    togglePermissions(permission);
  };

  const togglePassword = () => {
    if (openPassword) {
      clearSensitiveFields();
    }
    setOpenPassword(prev => !prev);
  };

  const togglePin = () => {
    if (openPin) {
      clearSensitiveFields();
    }
    setOpenPin(prev => !prev);
  };

  const themeOptions: ThemeItem[] = [
    { value: 'light', label: `${lang.GLOBAL_LIGHT}`, icon: <Icon.IconSun size={18} stroke={2} /> },
    { value: 'dark', label: `${lang.GLOBAL_DARK}`, icon: <Icon.IconMoon size={18} stroke={2} /> }
  ];

  return (
    <Container>
      <form
        onSubmit={form.onSubmit(async values => {
          const includeIf = <T extends object>(condition: boolean, fields: T): Partial<T> => (condition ? fields : {});

          const cleanedValues: IUser = {
            ...values,
            ...includeIf(!(openPassword && values.password && values.passwordConfirm), {
              password: undefined,
              passwordConfirm: undefined
            }),
            ...includeIf(!(openPin && values.pin && values.pinConfirm), {
              pin: undefined,
              pinConfirm: undefined
            }),
            uid: values.uid ? Number(values.uid) : null
          };
          permissionsToUser(selectedPermissions, cleanedValues);
          try {
            let res;
            switch (mode) {
              case UserFormMode.CREATE:
                res = await createUser(cleanedValues, new AbortController());
                break;
              case UserFormMode.UPDATE:
                res = await updateUser({ user: cleanedValues }, new AbortController());
                break;
              case UserFormMode.UPDATE_PROFILE:
                setPendingUserUpdate(cleanedValues);
                setOpenConfirmModal(true);
                return;
              default:
                throw new Error('Invalid form mode');
            }

            if (res?.statusOper?.code === 0) {
              onSuccess?.();
            } else {
              const errorMessage =
                mode === UserFormMode.CREATE
                  ? `${lang.ADD_USER_ALERT_UPDATE_ERROR} Code: ${res?.statusOper?.code} - ${res?.statusOper?.message}`
                  : `${lang.EDIT_USER_ALERT_UPDATE_ERROR} Code: ${res?.statusOper?.code} - ${res?.statusOper?.message}`;

              onError?.(errorMessage);
            }
          } catch (err: any) {
            onError?.(err.message || 'Unknown error');
          }
        })}
        className="user-form"
      >
        {pendingUserUpdate && mode === UserFormMode.UPDATE_PROFILE && (
          <UpdateUserConfirmation
            userData={pendingUserUpdate}
            lang={lang}
            openModal={openConfirmModal}
            toggleModal={toggleConfirmModal}
            onSubmit={async ({ currentPassword }: { currentPassword: string }) => {
              if (!pendingUserUpdate) return;
              try {
                const res = await updateUser({ user: pendingUserUpdate, currentPassword }, new AbortController());
                if (res?.statusOper?.code === 0) {
                  //TODO: make passowrd/pins collapse and clear values when update is successfull
                  clearSensitiveFields();
                  onSuccess?.();
                  setOpenPassword(false);
                  setOpenPin(false);
                } else {
                  onError?.(
                    `${lang.EDIT_USER_ALERT_UPDATE_ERROR} Code: ${res.statusOper.code} - ${res.statusOper.message}`
                  );
                }
              } catch (err: any) {
                onError?.(err.message || 'Unknown error');
              } finally {
                toggleConfirmModal();
                setPendingUserUpdate(null);
              }
            }}
          />
        )}

        <Grid>
          {/* Entid + name */}
          {mode === UserFormMode.UPDATE_PROFILE && (
            <GridCol span={12}>
              <TextInput
                label={lang.USER_ENTITY}
                value={user?.entid ? `${user.entid} - ${entities?.find(e => e.entid === user.entid)?.name ?? ''}` : ''}
                disabled
              />
            </GridCol>
          )}

          {/* ID */}
          <GridCol span={2}>
            <TextInput
              label={lang.USER_UID}
              //placeholder={isNew ? lang.ENTITY_AUTO_ENTID : ''}
              placeholder={isNew ? lang.USER_ID_PLACEHOLDER : ''}
              disabled={!isNew}
              required={isNew}
              {...form.getInputProps('uid')}
            />
          </GridCol>

          {/* Name */}
          <GridCol span={10}>
            <TextInput
              label={lang.GLOBAL_NAME}
              placeholder={isNew ? lang.USER_NAME_PLACEHOLDER : ''}
              {...form.getInputProps('name')}
            ></TextInput>
          </GridCol>

          {/* Mobile */}
          <GridCol span={{ base: 12, sm: 12, md: 4, lg: 4 }}>
            <TextInput
              label={lang.USER_MOBILE}
              placeholder={isNew ? lang.USER_MOBILE_PLACEHOLDER : ''}
              leftSection={<Icon.IconPhone stroke={2} />}
              {...form.getInputProps('mobile')}
            ></TextInput>
          </GridCol>

          {/* Email */}
          <GridCol span={{ base: 12, sm: 12, md: 4, lg: 4 }}>
            <TextInput
              label={lang.USER_EMAIL}
              placeholder={isNew ? lang.USER_EMAIL_PLACEHOLDER : ''}
              {...form.getInputProps('email')}
              error={form.values.email && !isValidEmail(form.values.email) ? 'Invalid email' : null}
              leftSection={<Icon.IconAt stroke={2} />}
            />
          </GridCol>

          {/* Card */}
          <GridCol span={{ base: 12, sm: 12, md: 4, lg: 4 }}>
            <TextInput
              label={lang.USER_CARD}
              placeholder={isNew ? lang.USER_CARD_PLACEHOLDER : ''}
              leftSection={<Icon.IconCreditCard stroke={2} />}
              {...form.getInputProps('card')}
            ></TextInput>
          </GridCol>

          {/* Language */}
          <GridCol span={6}>
            <Select
              label={lang.USER_LANG}
              data={languages.map(l => ({ value: l.langid.toString(), label: l.nativeName }))}
              value={form.values.lang.toString()}
              onChange={val => {
                const langId = val ? Number(val) : 0;
                form.setFieldValue('lang', langId);
                if (changeLanguage) {
                  changeLanguage(langId);
                }
              }}
              searchable
              allowDeselect={false}
            />
          </GridCol>

          {/* Region */}
          <GridCol span={6}>
            <Select
              label={lang.USER_REGION}
              data={[
                ...(regions?.map(region => ({
                  value: region,
                  label: region
                })) || [])
              ]}
              value={form.values.region}
              searchable={true}
              allowDeselect={false}
              {...form.getInputProps('region')}
            />
          </GridCol>

          {/* Password */}
          <GridCol span={6} className="password">
            {!openPassword && (
              <Button radius="md" onClick={togglePassword}>
                {lang.USER_CHANGE_PASSWORD}
              </Button>
            )}
            {openPassword && (
              <>
                <Title>Password</Title>

                <TextInput type="password" placeholder={lang.USER_NEW_PASSWORD} {...form.getInputProps('password')} />
                <TextInput
                  type="password"
                  placeholder={lang.USER_CONFIRM_PASSWORD}
                  {...form.getInputProps('passwordConfirm')}
                  error={form.errors.passwordConfirm}
                />

                <Button radius="md" onClick={togglePassword}>
                  <Icon.IconArrowUpBar />
                </Button>
              </>
            )}
          </GridCol>

          {/* Pin */}
          <GridCol span={6} className="pin">
            {!openPin && (
              <Button radius="md" onClick={togglePin}>
                {lang.USER_CHANGE_PIN}
              </Button>
            )}
            {openPin && (
              <>
                <Title>PIN</Title>

                <TextInput type="password" placeholder={lang.USER_NEW_PIN} {...form.getInputProps('pin')} />
                <TextInput
                  type="password"
                  placeholder={lang.USER_CONFIRM_PIN}
                  {...form.getInputProps('pinConfirm')}
                  error={form.errors.pinConfirm}
                />

                <Button radius="md" onClick={togglePin}>
                  <Icon.IconArrowUpBar />
                </Button>
              </>
            )}
          </GridCol>

          <Grid gutter="12vw">
            {/* Permissions on the left */}
            <GridCol span={6}>
              <div>
                <Title>{lang.USER_PERMISSIONS}</Title>
                {selectedPermissions.map(permission => (
                  <Checkbox
                    key={`${permission.appId}-${permission.mask}`}
                    label={getPermissionLabel(permission.langKey, lang)}
                    checked={permission.selected}
                    onChange={() => toggleSelection(permission)}
                    style={{ display: 'block', marginBottom: '0.5rem' }}
                    disabled={mode === UserFormMode.UPDATE_PROFILE}
                  />
                ))}
              </div>
            </GridCol>

            {/* Locations on top, Mode below on the right */}
            <GridCol span={6}>
              <div>
                <LocationTable
                  title={lang.GLOBAL_LOCATIONS || 'Locations'}
                  locations={locations}
                  currentEntid={currentEntid || 0}
                  editable={mode !== UserFormMode.UPDATE_PROFILE}
                  value={form.values.shops as number[]}
                  onChange={(newData: number[]) => form.setFieldValue('shops', newData)}
                />

                {mode === UserFormMode.UPDATE_PROFILE && (
                  <div style={{ marginTop: '1rem' }}>
                    <Select
                      className="theme-select"
                      label={lang.GLOBAL_MODE}
                      value={dark ? 'dark' : 'light'}
                      onChange={value => value && setColorScheme(value as 'light' | 'dark')}
                      data={themeOptions}
                      renderOption={({ option }) => (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {(option as ThemeItem).icon}
                          <span>{option.label}</span>
                        </div>
                      )}
                    />
                  </div>
                )}
              </div>
            </GridCol>
          </Grid>

          <Group>
            <Button className="save" radius="md" type="submit">
              {lang.GLOBAL_SAVE_BUTTON}
            </Button>
            {mode === UserFormMode.UPDATE && (
              <Button
                className="delete"
                radius="md"
                type="button"
                onClick={() => {
                  if (onDelete) onDelete(user.uid!);
                }}
              >
                {lang.GLOBAL_DELETE}
              </Button>
            )}
          </Group>
        </Grid>
      </form>
    </Container>
  );
};

export default UserForm;
